import { useState, useEffect } from "react";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function ServicesSection() {
  const [groups, setGroups]           = useState([]);
  const [activeGroup, setActiveGroup] = useState(0);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    fetchJSON(`${API_BASE}/services.php`)
      .then((data) => {
        setGroups(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Services load error:", err);
        setLoading(false);
      });
  }, []);

  const current  = groups[activeGroup] || null;
  const services = current?.services  || [];
  const row1 = services.slice(0, 3);
  const row2 = services.slice(3, 5);
  const showRow2Placeholders = services.length > 3;

  return (
    <section className="svc">
      <div className="svc__inner">

        {groups.length > 1 && (
          <div className="svc__tabs">
            {groups.map((g, i) => (
              <button
                key={g.link}
                className={`svc__tab${i === activeGroup ? " svc__tab--active" : ""}`}
                onClick={() => setActiveGroup(i)}
              >
                {g.category.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="svc__loading">Loading services…</div>
        ) : (
          <div className="svc__grid">

            {services.length <= 3 ? (
              /* ── COMPACT SINGLE ROW (e.g. Speciality with 3 services) ── */
              <div className="svc__row svc__row--compact">
                {row1.map((svc) => (
                  <ServiceCard key={svc.id} service={svc} size="tall" />
                ))}
                <div className="svc__cta-card">
                  <span className="svc__cta-eyebrow">
                    Get Solutions for Air Conditioner
                  </span>
                  <h3 className="svc__cta-heading">
                    Don't Waste a Second! Call Us to Solve Your Any Technical Problem
                  </h3>
                  <a href="/contact-us" className="svc__cta-btn">
                    FIND YOUR SOLUTION
                  </a>
                </div>
              </div>
            ) : (
              /* ── FULL TWO-ROW LAYOUT (4+ services) ── */
              <>
                <div className="svc__row svc__row--1">
                  <div className="svc__heading-card">
                    <span className="svc__eyebrow">
                      Services We Offer
                      <span className="svc__eyebrow-line" aria-hidden="true" />
                    </span>
                    <h2 className="svc__heading">
                      We Provide<br />
                      Full Range<br />
                      <em>{current?.category || "AC"} Services</em>
                    </h2>
                  </div>

                  {row1.map((svc) => (
                    <ServiceCard key={svc.id} service={svc} size="tall" />
                  ))}
                  {Array.from({ length: Math.max(0, 3 - row1.length) }).map((_, i) => (
                    <div key={`ph1-${i}`} className="svc__card svc__card--placeholder" />
                  ))}
                </div>

                <div className="svc__row svc__row--2">
                  {row2.length > 0 && row2.map((svc) => (
                    <ServiceCard key={svc.id} service={svc} size="wide" />
                  ))}
                  {showRow2Placeholders && Array.from({ length: Math.max(0, 2 - row2.length) }).map((_, i) => (
                    <div key={`ph2-${i}`} className="svc__card svc__card--placeholder" />
                  ))}
                  <div className="svc__cta-card">
                    <span className="svc__cta-eyebrow">
                      Get Solutions for Air Conditioner
                    </span>
                    <h3 className="svc__cta-heading">
                      Don't Waste a Second! Call Us to Solve Your Any Technical Problem
                    </h3>
                    <a href="/contact-us" className="svc__cta-btn">
                      FIND YOUR SOLUTION
                    </a>
                  </div>
                </div>
              </>
            )}

          </div>
        )}
      </div>

      <style>{`
        .svc {
          padding: 72px 0;
          background: #fff;
          overflow: hidden;
        }
        .svc__inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 40px;
        }

        /* ── TABS ── */
        .svc__tabs {
          display: flex;
          gap: 0;
          margin-bottom: 36px;
          border-bottom: 3px solid #013d97;
        }
        .svc__tab {
          padding: 10px 28px;
          border-radius: 0;
          border: none;
          border-top: 3px solid transparent;
          border-left: 1px solid #e0e4ef;
          background: #f4f6fb;
          font-size: 13px;
          font-weight: 800;
          color: #013d97;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
          letter-spacing: .08em;
          text-transform: uppercase;
          margin-bottom: -3px;
          position: relative;
        }
        .svc__tab:first-child { border-left: none; }
        .svc__tab--active {
          background: #013d97;
          color: #fff;
          border-top-color: #029add;
        }
        .svc__tab:hover:not(.svc__tab--active) {
          background: #029add;
          color: #fff;
        }

        .svc__loading {
          text-align: center;
          color: #5a5f72;
          padding: 60px 0;
          font-size: 16px;
        }

        .svc__grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        /* ── TWO-ROW LAYOUT ── */
        .svc__row--1 {
          display: grid;
          grid-template-columns: 1fr 1.15fr 1.15fr 1.15fr;
          gap: 16px;
          align-items: stretch;
        }
        .svc__row--2 {
          display: grid;
          grid-template-columns: 1.15fr 1.15fr 1fr;
          gap: 16px;
          align-items: stretch;
        }

        /* ── COMPACT SINGLE ROW (≤3 services) ── */
        .svc__row--compact {
          display: grid;
          grid-template-columns: 1.15fr 1.15fr 1.15fr 1fr;
          gap: 16px;
          align-items: stretch;
        }

        .svc__heading-card {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          padding: 10px 0 0;
        }
        .svc__eyebrow {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
          font-weight: 700;
          color: #029add;
          letter-spacing: .1em;
          text-transform: uppercase;
          margin-bottom: 18px;
        }
        .svc__eyebrow-line {
          display: inline-block;
          width: 36px;
          height: 2px;
          background: #029add;
          flex-shrink: 0;
        }
        .svc__heading {
          font-size: clamp(26px, 2.8vw, 42px);
          font-weight: 900;
          color: #013d97;
          line-height: 1.18;
          letter-spacing: -.5px;
          margin: 0;
        }
        .svc__heading em {
          font-style: normal;
          color: #029add;
        }

        /* ── SERVICE IMAGE CARD ── */
        .svc__card {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          display: block;
          text-decoration: none;
          background: #e6f0fb;
          min-height: 200px;
        }
        .svc__card--tall { aspect-ratio: 3/4; }
        .svc__card--wide { aspect-ratio: 4/3; }
        .svc__card--placeholder { opacity: .35; }

        .svc__card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition: transform 0.48s ease;
        }
        .svc__card:hover .svc__card-img {
          transform: scale(1.06);
        }

        .svc__card-scrim {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(1, 21, 70, 0.78) 0%,
            rgba(1, 21, 70, 0.22) 45%,
            transparent 100%
          );
          transition: opacity 0.35s;
          z-index: 1;
        }

        .svc__card-hover-bg {
          position: absolute;
          top: 66%;
          left: 0;
          right: 0;
          bottom: 0;
          background: #029add;
          transform: translateY(100%);
          transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 2;
        }
        .svc__card:hover .svc__card-hover-bg {
          transform: translateY(0%);
        }

        .svc__card-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 20px 20px 18px;
          font-size: 16px;
          font-weight: 800;
          color: #fff;
          letter-spacing: .02em;
          line-height: 1.25;
          z-index: 3;
          transition: opacity 0.2s, color 0.2s;
        }
        .svc__card:hover .svc__card-label {
          opacity: 0;
        }

        .svc__card-hover-content {
          position: absolute;
          top: 66%;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          padding: 16px 20px;
          z-index: 4;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.28s 0.08s ease, transform 0.28s 0.08s ease;
          pointer-events: none;
        }
        .svc__card:hover .svc__card-hover-content {
          opacity: 1;
          transform: translateY(0);
        }

        .svc__card-hover-bar {
          width: 36px;
          height: 3px;
          background: #fff;
          margin-bottom: 10px;
          flex-shrink: 0;
        }
        .svc__card-hover-title {
          font-size: 15px;
          font-weight: 900;
          color: #fff;
          line-height: 1.25;
          margin-bottom: 12px;
          letter-spacing: .01em;
        }
        .svc__card-hover-arrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #013d97;
          color: #fff;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
          padding: 7px 14px;
          border-radius: 0;
          text-decoration: none;
          transition: background 0.2s;
        }
        .svc__card-hover-arrow:hover { background: #0180b8; }
        .svc__card-hover-arrow svg {
          width: 13px;
          height: 13px;
          flex-shrink: 0;
        }

        /* ── CTA PANEL ── */
        .svc__cta-card {
          background: #013d97;
          border-radius: 12px;
          padding: 36px 32px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          gap: 18px;
          position: relative;
          overflow: hidden;
        }
        .svc__cta-card::before {
          content: "";
          position: absolute;
          bottom: -60px;
          right: -60px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          border: 40px solid rgba(2, 154, 221, 0.15);
          pointer-events: none;
        }
        .svc__cta-eyebrow {
          font-size: 12px;
          color: rgba(255,255,255,0.6);
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
        }
        .svc__cta-heading {
          font-size: clamp(17px, 1.6vw, 22px);
          font-weight: 800;
          color: #fff;
          line-height: 1.35;
          margin: 0;
          max-width: 300px;
          position: relative;
          z-index: 1;
        }
        .svc__cta-btn {
          display: inline-flex;
          align-items: center;
          background: #029add;
          color: #fff;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
          padding: 13px 28px;
          border-radius: 0;
          text-decoration: none;
          transition: background 0.2s, transform 0.2s;
          margin-top: 4px;
          position: relative;
          z-index: 1;
        }
        .svc__cta-btn:hover {
          background: #0180b8;
          transform: translateY(-2px);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .svc__inner { padding: 0 24px; }
          .svc__row--1 { grid-template-columns: 1fr 1fr 1fr; }
          .svc__heading-card { grid-column: 1 / -1; padding-bottom: 8px; }
          .svc__row--2 { grid-template-columns: 1fr 1fr; }
          .svc__cta-card { grid-column: 1 / -1; }
          .svc__row--compact { grid-template-columns: 1fr 1fr; }
          .svc__row--compact .svc__cta-card { grid-column: 1 / -1; }
        }

        @media (max-width: 680px) {
          .svc { padding: 44px 0; }
          .svc__inner { padding: 0 16px; }

          .svc__row--1, .svc__row--2 { grid-template-columns: 1fr 1fr; gap: 12px; }
          .svc__row--compact { grid-template-columns: 1fr 1fr; gap: 12px; }
          .svc__heading-card { grid-column: 1 / -1; }
          .svc__cta-card { grid-column: 1 / -1; padding: 24px 20px; }
          .svc__row--compact .svc__cta-card { grid-column: 1 / -1; }

          .svc__card--tall, .svc__card--wide { aspect-ratio: 4/3; min-height: 0; }
          .svc__card-label { font-size: 14px; padding: 14px 14px 12px; }
          .svc__card-hover-title { font-size: 13px; }
          .svc__card-hover-content,
          .svc__card-hover-bg { top: 70%; }

          .svc__cta-heading { max-width: 100%; }
          .svc__cta-btn {
            width: 100%;
            justify-content: center;
            text-align: center;
          }

          .svc__tabs {
            flex-wrap: wrap;
            border-bottom: none;
            gap: 8px;
            margin-bottom: 24px;
          }
          .svc__tab {
            border: 2px solid #013d97;
            border-radius: 4px;
            margin-bottom: 0;
            padding: 8px 18px;
            font-size: 12px;
          }
          .svc__tab--active { border-color: #013d97; }
        }

        @media (max-width: 480px) {
          .svc__inner { padding: 0 14px; }
          .svc__row--1, .svc__row--2, .svc__row--compact {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .svc__card--tall, .svc__card--wide { aspect-ratio: 16/10; }
          .svc__heading { font-size: clamp(24px, 7vw, 30px); }
          .svc__cta-card { padding: 22px 18px; }
          .svc__cta-card::before { width: 140px; height: 140px; bottom: -40px; right: -40px; }
          .svc__cta-heading { font-size: 17px; }
        }
      `}</style>
    </section>
  );
}

function ServiceCard({ service, size }) {
  const imgSrc = service.image8
    ? `${UPLOADS_URL}/services/${service.image8}`
    : null;

  return (
    <a
      href={`/services/${service.slug}`}
      className={`svc__card svc__card--${size}`}
      aria-label={service.title}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={service.title}
          className="svc__card-img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div
          className="svc__card-img"
          style={{ background: "linear-gradient(135deg,#013d97,#029add)" }}
        />
      )}

      <div className="svc__card-scrim" />
      <div className="svc__card-hover-bg" />
      <div className="svc__card-label">{service.title}</div>

      <div className="svc__card-hover-content" aria-hidden="true">
        <div className="svc__card-hover-bar" />
        <div className="svc__card-hover-title">{service.title}</div>
        <span className="svc__card-hover-arrow">
          VIEW SERVICE
          <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 7h10M8 3l4 4-4 4" stroke="#fff" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  );
}

export default ServicesSection;